import { computed, observable, runInAction } from "mobx"
import { useEffect } from "react"

import { useModel } from "@/hooks/useModel.ts"
import { batchInfo } from "@/lib/apiClient/client.ts"
import { Model } from "@/lib/Model.ts"

import type { TD_MemberProfile, TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"

export class UserTaskCountsModel extends Model {
  private readonly taskCounts = observable.box<{ total: number; successful: number } | null>(null)
  private disposed = false

  constructor(private readonly context: OrgContext, private readonly userId: TID) {
    super("UserTaskCountsModel")
  }

  readonly total = computed(() => this.taskCounts.get()?.total ?? 0)
  readonly successful = computed(() => this.taskCounts.get()?.successful ?? 0)

  init() {
    void this.fetchTaskCounts()
  }

  terminate() {
    this.disposed = true
    super.terminate?.()
  }

  private async fetchTaskCounts() {
    runInAction(() => this.isLoading.set(true))

    try {
      // TODO(dp): add a single API for user stats
      const [allTasks, successfulTasks] = await Promise.all([
        this.context.queries.listTasksForUser(this.userId, false, batchInfo(1)),
        this.context.queries.listTasksForUser(this.userId, true, batchInfo(1)),
      ])

      runInAction(() => this.isLoading.set(false))

      if (this.disposed) return

      runInAction(() => {
        this.taskCounts.set({
          total: allTasks?.totalCount ?? 0,
          successful: successfulTasks?.totalCount ?? 0,
        })
      })
    } catch {
      runInAction(() => this.isLoading.set(false))

      if (this.disposed) return
      runInAction(() => {
        this.taskCounts.set({ total: 0, successful: 0 })
      })
    }
  }
}

export function useUserTaskCounts(context: OrgContext, user: TD_MemberProfile): UserTaskCountsModel {
  const model = useModel(() => new UserTaskCountsModel(context, user.id), [context, user])

  useEffect(() => {
    model?.init()
  }, [model])

  return model
}